import { useState, useEffect, useMemo } from "react";
import { useData } from "../contexts/DataContext";

export const useDashboardData = () => {
  const { data: dashboardData } = useData();
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [yearRange, setYearRange] = useState<[number, number]>([2012, 2025]);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!dashboardData || !dashboardData.deals) {
          setLoading(false);
          return;
        }

        const processedDeals = dashboardData.deals as any[];
        setDeals(processedDeals);

        // Use year range from API filters
        if (dashboardData.filters?.years) {
          const { min, max } = dashboardData.filters.years;
          const startYear = Math.max(min, max - 10);
          setYearRange([startYear, max]);
        } else {
          // Fallback to calculating from deals
          const years = processedDeals
            .map((d) => d.Year as number | null)
            .filter((y): y is number => y !== null && !Number.isNaN(y));

          if (years.length) {
            const maxYear = Math.max(...years);
            const minYear = Math.min(...years);
            const startYear = Math.max(minYear, maxYear - 10);
            setYearRange([startYear, maxYear]);
          }
        }
      } catch (e) {
        console.error("Error processing data:", e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dashboardData]);

  const filterOptions = useMemo(() => {
    const yearRangeLimits = dashboardData?.filters?.years
      ? { min: dashboardData.filters.years.min, max: dashboardData.filters.years.max }
      : { min: 2012, max: 2025 };

    // Get currentHalf from API (H1 or H2) - indicates if max year has only first half data
    const currentHalf = dashboardData?.filters?.years?.currentHalf as "H1" | "H2" | undefined;

    if (!deals.length) {
      return {
        phases: [] as string[],
        dealYears: [] as number[],
        industries: [] as (string | null)[],
        cantons: [] as string[],
        ceoGenders: [] as (string | null)[],
        yearRangeLimits,
        currentHalf,
      };
    }
    return {
      phases: [...new Set(deals.map((d) => d.Phase).filter(Boolean))].sort(),
      dealYears: [...new Set(deals.map((d) => d.Year).filter(Boolean))].sort(),
      industries: [...new Set(deals.map((d) => d.Industry).filter(Boolean))].sort(),
      cantons: [...new Set(deals.map((d) => d.Canton).filter(Boolean))].sort(),
      ceoGenders: [...new Set(deals.map((d) => d["Gender CEO"]).filter(Boolean))].sort(),
      yearRangeLimits,
      currentHalf,
    };
  }, [deals, dashboardData]);

  return {
    deals,
    loading,
    filterOptions,
    initialYearRange: yearRange,
  };
};
